import { useState } from 'react';
import { useAdminList } from './useAdminList';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { AdminDevice } from '../../../domain/Admin';

export function useAdminDevices() {
  const { admin } = useRepositories();
  const list = useAdminList((params) => admin.fetchDevices(params).then((r) => ({ items: r.devices, total: r.total })));
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<AdminDevice | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminDevice | null>(null);
  const [transferTarget, setTransferTarget] = useState<AdminDevice | null>(null);
  const [shareTarget, setShareTarget] = useState<AdminDevice | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const runAction = async (id: string, action: () => Promise<unknown>, done?: string) => {
    setBusyId(id);
    setActionError(null);
    try {
      await action();
      if (done) setNotice(done);
      await list.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'No se pudo completar la acción.');
    } finally {
      setBusyId(null);
    }
  };

  const handleRename = (name: string) => {
    if (!renameTarget || !name.trim()) return;
    runAction(renameTarget.id, () => admin.updateDevice(renameTarget.id, { name: name.trim() }));
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    runAction(deleteTarget.id, () => admin.deleteDevice(deleteTarget.id));
  };

  const handleTransfer = (email: string) => {
    const target = transferTarget;
    if (!target || !email.trim()) return;
    runAction(target.id, () => admin.transferDevice(target.id, email.trim()), `"${target.name}" ahora es de ${email.trim()}.`);
  };

  const handleShare = (email: string, level: 'view' | 'control') => {
    const target = shareTarget;
    if (!target || !email.trim()) return;
    runAction(target.id, () => admin.shareDevice(target.id, email.trim(), level), `${email.trim()} ya puede ver "${target.name}".`);
  };

  return {
    devices: list.items,
    filtered: list.items,
    total: list.total,
    loadingMore: list.loadingMore,
    loadMore: list.loadMore,
    query: list.query,
    setQuery: list.setQuery,
    loading: list.loading,
    error: actionError ?? list.error,
    busyId,
    renameTarget,
    setRenameTarget,
    deleteTarget,
    setDeleteTarget,
    runAction,
    handleRename,
    handleDelete,
    transferTarget,
    setTransferTarget,
    handleTransfer,
    shareTarget,
    setShareTarget,
    handleShare,
    notice,
    clearNotice: () => setNotice(null),
    restoreDevice: (id: string) => runAction(id, () => admin.restoreDevice(id)),
  };
}
